// Spacer deadlines: a spacer's deadline applies to every item in its section
// (the items below it, up to the next spacer). Items before the first spacer
// belong to no spacer. All helpers are pure - they return new item arrays
// (in the same order) and never change the ones passed in.
// Kept identical in client/ and mobile/.

// The calendar day of a deadline as "YYYY-MM-DD" (or null). Deadlines are
// picked as "YYYY-MM-DD" and come back from the server as UTC midnight
// ("YYYY-MM-DDT00:00:00.000Z"), so the UTC date is the picked day.
export const deadlineDay = (deadline) => {
  if (!deadline) return null;
  if (typeof deadline === "string" && /^\d{4}-\d{2}-\d{2}/.test(deadline)) {
    return deadline.slice(0, 10);
  }
  const date = new Date(deadline);
  return Number.isNaN(date.getTime()) ? null : date.toISOString().slice(0, 10);
};

export const sameDeadline = (a, b) => deadlineDay(a) === deadlineDay(b);

// Index of an item given by _id, or the index itself when given a number
const toIndex = (items, ref) =>
  typeof ref === "number" ? ref : items.findIndex((i) => i._id === ref);

// The spacer whose section the item at `index` is in (null if none).
// For a spacer, that's the spacer itself.
export const getSectionSpacer = (items, index) => {
  for (let i = index; i >= 0; i--) {
    if (items[i]?.isSpacer) return items[i];
  }
  return null;
};

// Set the deadline of every non-spacer item in the spacer's section
const setSectionDeadline = (items, spacerIndex, deadline) => {
  const result = [...items];
  for (let i = spacerIndex + 1; i < result.length && !result[i].isSpacer; i++) {
    if (!sameDeadline(result[i].deadline, deadline)) {
      result[i] = { ...result[i], deadline: deadline || null };
    }
  }
  return result;
};

// Rule: setting/changing a spacer's deadline sets it on every item in its
// section; removing it (null) removes the deadline from them.
// `spacerRef` is the spacer's _id or its index.
export const applySpacerDeadline = (items, spacerRef, deadline) => {
  const spacerIndex = toIndex(items, spacerRef);
  if (spacerIndex < 0 || !items[spacerIndex]?.isSpacer) return items;
  const result = setSectionDeadline(items, spacerIndex, deadline);
  result[spacerIndex] = { ...result[spacerIndex], deadline: deadline || null };
  return result;
};

// Deadlines after the item `movedItemId` was moved within a list
// (itemsBefore -> itemsAfter):
// - An item moved into a section whose spacer has a deadline gets it.
// - An item with a deadline moved out of a section whose spacer has a
//   deadline into one without: askKeepRemove is true - ask the user whether
//   to keep its deadline (see removeItemDeadline).
// - Moving a spacer: every item that is now in another section whose spacer
//   has a deadline gets it (items left behind otherwise keep their deadline).
// - Reordering within the same section changes nothing.
export const applyMoveDeadlines = (itemsBefore, itemsAfter, movedItemId) => {
  const noChange = { items: itemsAfter, askKeepRemove: false };
  const newIndex = itemsAfter.findIndex((i) => i._id === movedItemId);
  if (newIndex === -1) return noChange;
  const moved = itemsAfter[newIndex];

  if (moved.isSpacer) {
    const sectionBefore = new Map();
    itemsBefore.forEach((item, index) => {
      if (!item.isSpacer) {
        sectionBefore.set(item._id, getSectionSpacer(itemsBefore, index)?._id);
      }
    });
    let items = itemsAfter;
    itemsAfter.forEach((item, index) => {
      if (item.isSpacer) return;
      const spacer = getSectionSpacer(itemsAfter, index);
      if (
        spacer?.deadline &&
        spacer._id !== sectionBefore.get(item._id) &&
        !sameDeadline(item.deadline, spacer.deadline)
      ) {
        if (items === itemsAfter) items = [...itemsAfter];
        items[index] = { ...item, deadline: spacer.deadline };
      }
    });
    return { items, askKeepRemove: false };
  }

  const oldIndex = itemsBefore.findIndex((i) => i._id === movedItemId);
  const oldSpacer =
    oldIndex === -1 ? null : getSectionSpacer(itemsBefore, oldIndex);
  const newSpacer = getSectionSpacer(itemsAfter, newIndex);
  if ((oldSpacer?._id ?? null) === (newSpacer?._id ?? null)) return noChange;

  if (newSpacer?.deadline) {
    if (sameDeadline(moved.deadline, newSpacer.deadline)) return noChange;
    const items = [...itemsAfter];
    items[newIndex] = { ...moved, deadline: newSpacer.deadline };
    return { items, askKeepRemove: false };
  }

  return {
    items: itemsAfter,
    askKeepRemove: !!(oldSpacer?.deadline && moved.deadline),
  };
};

// Rule: a new item added into a section whose spacer has a deadline gets it.
// `newItemRefs` is the new item's _id or index - or an array of them.
export const applyNewItemDeadline = (items, newItemRefs) => {
  const refs = Array.isArray(newItemRefs) ? newItemRefs : [newItemRefs];
  let result = items;
  refs.forEach((ref) => {
    const index = toIndex(items, ref);
    if (index < 0 || !items[index] || items[index].isSpacer) return;
    const spacer = getSectionSpacer(items, index);
    if (
      spacer?.deadline &&
      !sameDeadline(items[index].deadline, spacer.deadline)
    ) {
      if (result === items) result = [...items];
      result[index] = { ...items[index], deadline: spacer.deadline };
    }
  });
  return result;
};

// The items with the given item's deadline removed (the "Remove deadline"
// answer after moving it out of a deadline section)
export const removeItemDeadline = (items, itemId) =>
  items.map((item) =>
    item._id === itemId ? { ...item, deadline: null } : item,
  );
